import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

import {
  getTicket,
  getTickets,
  getInfiniteTickets,
  createTicket as createTicketRequest,
  updateTicket as updateTicketRequest,
} from "@Http/tickets";
import { MutationError } from "@Types/index";
import { HttpQuery, PaginatedResult } from "@Types/http";
import { CreateTicketRequest, LenderTicket } from "@Types/tickets";

const useGetTickets = (query?: HttpQuery) => {
  const queryKey = ["tickets", query ?? []];

  const { data, isLoading, error } = useQuery<
    PaginatedResult<LenderTicket[]>,
    MutationError,
    PaginatedResult<LenderTicket[]>
  >({
    queryKey: queryKey,
    queryFn: getTickets(query),
  });

  return {
    data: data?.docs ?? [],
    totalPages: data?.totalPages ?? 0,
    totalDocs: data?.totalDocs ?? 0,
    isLoading,
    error,
  };
};

export const useGetInfiniteTickets = (query?: HttpQuery) => {
  const queryKey = ["tickets", query ?? []];

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery<
    PaginatedResult<LenderTicket[]>,
    MutationError,
    PaginatedResult<LenderTicket[]>
  >({
    queryKey: queryKey,
    queryFn: getInfiniteTickets(query),
    getNextPageParam: (lastPage) => {
      return lastPage?.nextPage || undefined;
    },
  });

  return {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
  };
};

export const useGetTicket = (id: string) => {
  const queryKey = [`ticket-${id}`];

  const {
    data: ticket,
    isLoading,
    error,
    isSuccess,
  } = useQuery({
    queryKey: queryKey,
    queryFn: getTicket(id),
  });

  return {
    isSuccess,
    ticket,
    isLoading,
    error,
  };
};

export const useUpdateTicket = () => {
  const mutation = useMutation<LenderTicket, MutationError, LenderTicket>(
    updateTicketRequest(),
    {
      onSuccess: () => {
        toast.success("Successfully updated a ticket.", {
          position: "top-center",
        });
      },
    }
  );
  return {
    updateTicket: mutation.mutate,
    loading: mutation.isLoading,
    success: mutation.isSuccess,
    error: mutation.error,
  };
};

export const useCreateTicket = () => {
  const navigate = useNavigate();
  const mutation = useMutation<
    LenderTicket,
    MutationError,
    CreateTicketRequest
  >(createTicketRequest(), {
    onSuccess: (data) => {
      toast.success("Successfully created a ticket.", {
        position: "top-center",
      });
      navigate(`/tickets/${data._id}`, { replace: true });
    },
  });
  return {
    createTicket: mutation.mutate,
    loading: mutation.isLoading,
    success: mutation.isSuccess,
    error: mutation.error,
  };
};

export default useGetTickets;
